import { Link } from "react-router-dom";
import { Target, PiggyBank, Receipt, CalendarDays, Mic, Ban } from "lucide-react";
import { cn } from "@/lib/cn";
import styles from "./PremiumAdCreative.module.css";

const PARTICLES = [
  { top: "10%", left: "8%", size: 3 },
  { top: "18%", left: "85%", size: 2 },
  { top: "58%", left: "94%", size: 3 },
  { top: "82%", left: "5%", size: 2 },
  { top: "38%", left: "50%", size: 2 },
  { top: "90%", left: "62%", size: 3 },
  { top: "6%", left: "45%", size: 2 },
  { top: "70%", left: "30%", size: 2 },
  { top: "28%", left: "20%", size: 2 },
  { top: "48%", left: "75%", size: 2 },
  { top: "66%", left: "12%", size: 3 },
  { top: "94%", left: "40%", size: 2 },
  { top: "2%", left: "62%", size: 2 },
  { top: "14%", left: "32%", size: 3 },
  { top: "24%", left: "68%", size: 2 },
  { top: "34%", left: "8%", size: 2 },
  { top: "44%", left: "92%", size: 3 },
  { top: "54%", left: "38%", size: 2 },
  { top: "64%", left: "58%", size: 3 },
  { top: "74%", left: "82%", size: 2 },
  { top: "84%", left: "22%", size: 2 },
  { top: "96%", left: "70%", size: 3 },
];

interface PremiumAdCreativeProps {
  variant: "compact" | "full";
  href?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  className?: string;
}

export function PremiumAdCreative({ variant, href, ctaLabel, onCtaClick, className }: PremiumAdCreativeProps) {
  const isFull = variant === "full";

  return (
    <div className={cn(styles.creative, isFull ? styles.full : styles.compact, className)}>
      <span className={cn(styles.bgGrid, "dotGrid")} />
      <span className={cn(styles.glow, styles.glowCyan)} />
      <span className={cn(styles.glow, styles.glowBlue)} />
      <span className={cn(styles.glow, styles.glowMint)} />
      <svg className={styles.circuit} viewBox="0 0 400 200" preserveAspectRatio="none">
        <path d="M0 26 L54 26 L54 8 L150 8" />
        <path d="M400 176 L342 176 L342 194 L246 194" />
        <circle cx="54" cy="26" r="2" />
        <circle cx="150" cy="8" r="1.6" />
        <circle cx="342" cy="176" r="2" />
        <circle cx="246" cy="194" r="1.6" />
      </svg>
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className={styles.particle}
          style={{ top: p.top, left: p.left, width: p.size, height: p.size, animationDelay: `${i * 0.3}s` }}
        />
      ))}
      <span className={styles.sheen} />

      <div className={styles.content}>
        <div className={styles.mainCol}>
          <div className={styles.panel}>
            <FlyerCard isFull={isFull} />
          </div>

          {href && (
            <Link to={href} onClick={onCtaClick} className={styles.ctaBelow}>
              {ctaLabel ?? "Ver planos"}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

const FEATURE_CARDS = [
  { icon: CalendarDays, label: "Calendário", desc: "Gastos dia a dia" },
  { icon: Target, label: "Metas", desc: "Alcance objetivos" },
  { icon: PiggyBank, label: "Orçamento", desc: "Por categoria" },
  { icon: Receipt, label: "Contas", desc: "Não perca prazos" },
  { icon: Mic, label: "Fale com a IA", desc: "Anote por voz" },
  { icon: Ban, label: "Sem anúncios", desc: "Experiência limpa" },
];

function FlyerCard({ isFull }: { isFull: boolean }) {
  return (
    <div className={styles.flyerGrid}>
      {FEATURE_CARDS.map((f, i) => (
        <div key={f.label} className={styles.flyerTile} style={{ animationDelay: `${i * 0.08}s` }}>
          <span className={styles.flyerIcon} style={{ animationDelay: `${i * 0.25}s` }}>
            <f.icon size={isFull ? 22 : 15} strokeWidth={2.2} />
          </span>
          <div className={styles.flyerTileText}>
            <span className={styles.flyerTileLabel}>{f.label}</span>
            <span className={styles.flyerTileDesc}>{f.desc}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
